import React, { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/content";
import { endpoints } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Backdrop } from "@/components/IntersectMotif";

const FIELDS = [
  ["company", "Company", true], ["website", "Website", false], ["founder_name", "Founder Name", true],
  ["founder_email", "Founder Email", true], ["headquarters", "Headquarters", false], ["sector", "Sector", false],
  ["company_stage", "Company Stage", false], ["funding_stage", "Funding Stage", false],
  ["amount_raising", "Amount Being Raised", false], ["current_revenue", "Current Revenue", false],
  ["saudi_presence", "Saudi Market Presence", false], ["saudi_interest", "Saudi Expansion Interest", false],
];
const AREAS = [
  ["investment_summary", "Investment Summary"], ["partnership_opportunity", "Strategic Partnership Opportunity"], ["why_intersect", "Why Intersect?"],
];

export default function Opportunity() {
  const { t } = useLang();
  const [form, setForm] = useState(Object.fromEntries([...FIELDS, ...AREAS].map(([k]) => [k, ""])));
  const [deck, setDeck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (deck) fd.append("pitch_deck", deck);
      await endpoints.opportunity(fd);
      setDone(true);
      toast.success("Opportunity submitted. Thank you.");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHero eyebrow="Present an Opportunity" title="Present an opportunity to Intersect." image={IMG.abstractGold}
        subtitle="Share your company, venture or partnership opportunity. Submissions are stored securely and reviewed by our team." />

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.kingdomSunset} opacity={0.12} />
        <div className="container-x max-w-3xl relative z-10">
          {done ? (
            <Reveal>
              <div className="bg-charcoal/75 backdrop-blur-xl border border-primary-gold/20 p-10 md:p-12 text-center rounded-sm" data-testid="opportunity-success">
                <h3 className="text-2xl font-light text-ivory">{t("Thank you.")}</h3>
                <p className="mt-4 text-muted-text">{t("Your opportunity has been received. We review every submission and will reach out if there is a fit.")}</p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="space-y-8 bg-charcoal/75 backdrop-blur-xl border border-primary-gold/15 p-6 md:p-8 rounded-sm" data-testid="opportunity-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {FIELDS.map(([k, label, req]) => (
                    <input key={k} required={req} placeholder={t(label) + (req ? " *" : "")} className="field" value={form[k]} onChange={upd(k)} data-testid={`opp-${k}`} />
                  ))}
                </div>
                {AREAS.map(([k, label]) => (
                  <div key={k}>
                    <label className="eyebrow block mb-3">{t(label)}</label>
                    <textarea rows={3} className="field resize-none" value={form[k]} onChange={upd(k)} data-testid={`opp-${k}`} />
                  </div>
                ))}
                <div>
                  <label className="eyebrow block mb-3">{t("Pitch Deck (PDF, max 25MB)")}</label>
                  <input type="file" accept=".pdf,.ppt,.pptx,.key,.doc,.docx" onChange={(e) => setDeck(e.target.files[0])}
                    className="block w-full text-sm text-muted-text file:mr-4 file:py-2.5 file:px-6 file:border file:border-primary-gold file:bg-transparent file:text-primary-gold file:text-xs file:uppercase file:tracking-wide hover:file:bg-primary-gold hover:file:text-obsidian file:cursor-pointer file:transition-colors" data-testid="opp-deck" />
                </div>
                <button type="submit" disabled={busy} className="btn-solid" data-testid="opportunity-submit">{busy ? t("Submitting…") : t("Submit Opportunity")}</button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
